import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { fetchAdminStats } from './services/adminService';
import UserStats from './components/UserStats';
import DocumentStats from './components/DocumentStats';
import DailyStats from './components/DailyStats';
import { collection, getDocs, doc, getDoc, query, where } from "firebase/firestore";
import { db } from "../../config/firebaseConfig";
import './Admin.css';

const Admin = () => {
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const { user, setTempUser } = useAuth();
  const navigate = useNavigate();
  const [activeTab, setActiveTab] = useState('dashboard');

  // User context switching state
  const [userContext, setUserContext] = useState(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [searchResults, setSearchResults] = useState([]);
  const [adminEmail, setAdminEmail] = useState(null);

  useEffect(() => {
    // Check if user is admin
    const checkAdminAccess = async () => {
      // If user is not logged in, redirect to login
      if (!user) {
        navigate('/login');
        return;
      }

      try {
        // Fetch admin stats
        const adminData = await fetchAdminStats(user.uid);
        setStats(adminData);
        setLoading(false);
      } catch (err) {
        console.error('Error fetching admin data:', err);
        setError('You do not have permission to access the admin panel or there was an error fetching data.');
        // If not admin, redirect to dashboard after a delay
        setTimeout(() => navigate('/dashboard'), 3000);
        setLoading(false);
      }
    };

    const fetchAdminEmail = async () => {
      try {
        const docRef = doc(db, "admin", "IStA6fJzjiHovjcz6B0V");
        const docSnap = await getDoc(docRef);
        if (docSnap.exists()) {
          const data = docSnap.data();
          setAdminEmail(data.adminUser[0]);
        } else {
          console.log("No such document!");
        }
      } catch (error) {
        console.error("Error fetching admin email: ", error);
      }
    };

    checkAdminAccess();
    fetchAdminEmail();

    // Check for stored user context
    const storedUserId = localStorage.getItem('selectedUserId');
    if (storedUserId) {
      handleUserSelect(storedUserId);
    }
  }, [user, navigate]);

  const handleUserSelect = async (userId) => {
    const userDoc = await getDoc(doc(db, "users", userId));
    const selectedUser = { id: userId, ...userDoc.data() };
    setUserContext(selectedUser);
    setTempUser({ uid: userId, ...userDoc.data() }); // Set temporary user
    localStorage.setItem('selectedUserId', userId); // Store user ID in local storage
    setActiveTab('userContext'); // Switch to user context tab
  };

  const handleExitUserContext = () => {
    setUserContext(null);
    setTempUser(null); // Reset temporary user
    localStorage.removeItem('selectedUserId'); // Remove user ID from local storage
    setActiveTab('dashboard'); // Switch back to dashboard tab
  };

  const handleSearch = async (e) => {
    e.preventDefault();
    if (searchQuery.trim() === '') return;

    const usersCollection = collection(db, "users");
    const q = query(usersCollection, where('email', '==', searchQuery));
    const querySnapshot = await getDocs(q);

    const results = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
    setSearchResults(results);
  };

  if (loading) {
    return (
      <div className="admin-loading">
        <div className="spinner"></div>
        <p>Loading admin dashboard...</p>
      </div>
    );
  }

  if (error) {
    return (
      <div className="admin-error">
        <h2>Access Denied</h2>
        <p>{error}</p>
        <p>Redirecting to dashboard...</p>
      </div>
    );
  }

  return (
    <div className="admin-dashboard">
      <div className="admin-header">
        <h1>Admin Dashboard</h1>
        <div className="admin-nav">
          <button 
            className={`admin-nav-btn ${activeTab === 'dashboard' ? 'active' : ''}`} 
            onClick={() => setActiveTab('dashboard')}
          >
            Dashboard
          </button>
          <button 
            className={`admin-nav-btn ${activeTab === 'switchUser' ? 'active' : ''}`} 
            onClick={() => setActiveTab('switchUser')}
          >
            Switch User
          </button>
          {userContext && (
            <button 
              className={`admin-nav-btn ${activeTab === 'userContext' ? 'active' : ''}`} 
              onClick={() => setActiveTab('userContext')}
            >
              User: {userContext.email}
            </button>
          )}
        </div>
        <p>Welcome, {user.email}</p>
      </div>

      <div className="admin-content">
        {activeTab === 'dashboard' && (
          <div className="admin-grid">
            <DailyStats 
              dailyNewUsers={stats.dailyNewUsers}
              dailyNewSubscribers={stats.dailyNewSubscribers}
              dailySubscriptionAmount={stats.dailySubscriptionAmount}
              monthlySubscriptionAmount={stats.monthlySubscriptionAmount}
            />
            
            <UserStats 
              totalUsers={stats.totalUsers}
              newUsersWeek={stats.newUsersWeek}
              newUsersMonth={stats.newUsersMonth}
              activeSubscribers={stats.activeSubscribers}
              conversionRate={stats.conversionRate}
            />
            
            <DocumentStats 
              totalResumes={stats.totalResumes}
              totalCoverLetters={stats.totalCoverLetters}
            />
          </div>
        )}

        {activeTab === 'switchUser' && (
          <div className="user-search-panel">
            <form onSubmit={handleSearch} className="search-form">
              <input
                type="text"
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                placeholder="Search by user email"
                className="search-input"
              />
              <button type="submit" className="search-button">Search</button>
            </form>
            
            <div className="search-results">
              <h2>Search Results</h2>
              {searchResults.length > 0 ? (
                <div className="user-list">
                  {searchResults.map(user => (
                    <div key={user.id} className="user-item">
                      <span className="user-email">{user.email}</span>
                      <button 
                        onClick={() => handleUserSelect(user.id)} 
                        className="switch-button"
                      >
                        Switch to User
                      </button>
                    </div>
                  ))}
                </div>
              ) : (
                <p className="no-results">No users found</p>
              )}
            </div>
          </div>
        )}

        {activeTab === 'userContext' && userContext && (
          <UserContextView user={userContext} onExit={handleExitUserContext} />
        )}
      </div>
    </div>
  );
};

const UserContextView = ({ user, onExit }) => {
  const [resumes, setResumes] = useState([]);
  const [coverLetters, setCoverLetters] = useState([]);
  const navigate = useNavigate();

  useEffect(() => {
    const fetchUserData = async () => {
      try {
        const resumesCollection = collection(db, `users/${user.id}/resumes`);
        const resumesSnapshot = await getDocs(resumesCollection);
        setResumes(resumesSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));

        const coverLettersCollection = collection(db, `users/${user.id}/cover-letters`);
        const coverLettersSnapshot = await getDocs(coverLettersCollection);
        setCoverLetters(coverLettersSnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() })));
      } catch (error) {
        console.error("Error fetching user data:", error);
      }
    };
    
    fetchUserData();
  }, [user]);

  const editResume = (resumeId) => {
    navigate(`/editor/resume/${resumeId}`);
  };

  const editCoverLetter = (coverLetterId) => {
    navigate(`/editor/cover-letter/${coverLetterId}`);
  };

  return (
    <div className="user-context-view">
      <div className="user-context-header">
        <h2>User: {user.email}</h2>
        <button onClick={onExit} className="exit-context-button">Exit User Context</button>
      </div>
      
      <div className="user-documents">
        <div className="document-section">
          <h3>Resumes</h3>
          {resumes.length > 0 ? (
            <div className="documents-grid">
              {resumes.map(resume => (
                <div key={resume.id} className="document-card">
                  <div className="document-preview">
                    {/* Assume ResumePreview is a component you have */}
                    {/* <ResumePreview resume={resume} /> */}
                    <div className="preview-placeholder">
                      Resume Preview: {resume.title || "Untitled Resume"}
                    </div>
                  </div>
                  <div className="document-info">
                    <h4>{resume.title || "Untitled Resume"}</h4>
                    <p>Last edited: {resume.lastUpdated ? new Date(resume.lastUpdated.seconds * 1000).toLocaleDateString() : "Unknown"}</p>
                    <button onClick={() => editResume(resume.id)} className="edit-button">Edit</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-documents">No resumes found</p>
          )}
        </div>
        
        <div className="document-section">
          <h3>Cover Letters</h3>
          {coverLetters.length > 0 ? (
            <div className="documents-grid">
              {coverLetters.map(letter => (
                <div key={letter.id} className="document-card">
                  <div className="document-preview">
                    {/* Assume CoverLetterPreview is a component you have */}
                    {/* <CoverLetterPreview letter={letter} /> */}
                    <div className="preview-placeholder">
                      Cover Letter Preview: {letter.title || "Untitled Cover Letter"}
                    </div>
                  </div>
                  <div className="document-info">
                    <h4>{letter.title || "Untitled Cover Letter"}</h4>
                    <p>Last edited: {letter.lastUpdated ? new Date(letter.lastUpdated.seconds * 1000).toLocaleDateString() : "Unknown"}</p>
                    <button onClick={() => editCoverLetter(letter.id)} className="edit-button">Edit</button>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="no-documents">No cover letters found</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default Admin;